/* eslint-disable react/forbid-prop-types */
import React, { useState, useMemo, useCallback } from 'react';
import {
  Row,
  Col,
  PageHeader,
  Menu,
} from 'antd';

import PropTypes from 'prop-types';
import FilterNode from './FilterNode';
import FilterNodeRedesign from './FilterNodeRedesign';

function flattenNodes(nodes) {
  return nodes.reduce(
    (acc, n) => acc.concat(n, flattenNodes(n.children || [])),
    [],
  );
}

export default function FilterDropdown({
  onFilter,
  filters,
  activeFilters,
  style = {},
  useRedesign = false,
}) {
  const allNodes = useMemo(
    () => filters
      .flat(2)
      .flatMap((root) => flattenNodes([root])),
    [filters],
  );

  const flatFilters = useMemo(() => filters.flat(2), [filters]);

  const [expandedKeys, setExpandedKeys] = useState(
    () => new Set(allNodes.map((n) => n.key)),
  );

  const toggleExpand = useCallback((key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  if (!useRedesign) {
    return (
      <div>
        <Row>
          <PageHeader
            title="Filters"
            style={{ padding: 0, marginBottom: 5, fontFamily: 'raleway-medium' }}
          />
        </Row>
        <div
          style={{
            overflowY: 'auto',
            maxHeight: '65vh',
            maxWidth: 600,
            boxSizing: 'border-box',
            paddingRight: 8,
          }}
        >
          {filters.map((filterRow, rowIndex) => {
            const rowKey = filterRow
              .map((treeData) => treeData[0].key)
              .join('-');

            return (
              <Row
                justify="left"
                key={rowKey}
                style={{ marginTop: rowIndex > 0 ? 5 : 0, ...style }}
              >
                {filterRow.map((treeData) => {
                  const category = treeData[0].key;
                  const activeSet = activeFilters[category] instanceof Set
                    ? activeFilters[category]
                    : new Set();
                  return (
                    <Col key={category}>
                      {treeData.map((rootNode) => (
                        <FilterNode
                          key={rootNode.key}
                          node={rootNode}
                          activeSet={activeSet}
                          onFilterChange={onFilter}
                          category={category}
                          level={0}
                          expandedKeys={expandedKeys}
                          toggleExpand={toggleExpand}
                        />
                      ))}
                    </Col>
                  );
                })}
              </Row>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div>
      <Menu style={{ borderRight: 'none' }} className="filter-dropdown-menu">
        {flatFilters.map((filter) => {
          const category = filter.key;
          const activeSet = activeFilters[category] instanceof Set
            ? activeFilters[category]
            : new Set();

          return (
            <Menu.SubMenu key={filter.key} title={filter.title} className="filter-dropdown-menu-item">
              <FilterNodeRedesign
                key={filter.key}
                node={filter}
                activeSet={activeSet}
                onFilterChange={onFilter}
                category={category}
              />
            </Menu.SubMenu>
          )
        })}
      </Menu>
    </div>
  )
}

FilterDropdown.propTypes = {
  onFilter: PropTypes.func.isRequired,
  filters: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string.isRequired,
          key: PropTypes.string.isRequired,
          children: PropTypes.array,
        }),
      ),
    ),
  ).isRequired,
  activeFilters: PropTypes.objectOf(PropTypes.instanceOf(Set)),
  style: PropTypes.object,
  useRedesign: PropTypes.bool,
};

FilterDropdown.defaultProps = {
  activeFilters: {},
  style: {},
  useRedesign: false,
};
